/**
 * Centralized, mobile-safe share launching utilities.
 *
 * Rules:
 * - SMS always uses `window.location.href` (never `window.open`) to avoid about:blank on mobile.
 * - External URLs use `window.open` with `noopener,noreferrer`, falling back to `window.location.href`.
 * - Clipboard uses async API with `execCommand` fallback.
 * - Every action surfaces a toast on failure so the user is never stranded.
 */

import { toast } from 'sonner';

/**
 * Mobile-safe SMS launch. Uses location.href which hands off to the OS messaging app.
 * iOS requires `sms:&body=` (or `sms:;body=`) while Android uses `sms:?body=`.
 *
 * Returns a Promise that resolves once the OS has had time to intercept the
 * protocol handler. The caller MUST await this before triggering any React
 * state updates or navigations — otherwise the browser will cancel the `sms:`
 * navigation in favour of the DOM mutation.
 */
export function openSms(body: string): Promise<{ didLeave: boolean }> {
  return new Promise((resolve) => {
    const encoded = encodeURIComponent(body);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const separator = isIOS ? '&' : '?';

    // Track if the app actually switches away (protocol handler fired)
    let didLeave = false;
    const onVisChange = () => {
      if (document.hidden) {
        didLeave = true;
      }
    };
    document.addEventListener('visibilitychange', onVisChange);

    window.location.href = `sms:${separator}body=${encoded}`;

    // Give the OS 1.5 seconds to intercept the protocol handler.
    // Do NOT resolve sooner — React re-renders within this window kill the handoff.
    setTimeout(() => {
      document.removeEventListener('visibilitychange', onVisChange);

      if (!didLeave) {
        // Protocol handler didn't fire — fallback
        if (navigator.share) {
          navigator.share({ text: body }).catch(() => {});
        } else {
          navigator.clipboard?.writeText(body);
          toast.info('Message copied — paste it in your messaging app');
        }
      }

      resolve({ didLeave });
    }, 1500);
  });
}

/**
 * Open an external URL (social platform, etc.) in a new tab.
 * Falls back to same-tab navigation if popup is blocked.
 */
export function openExternal(url: string): void {
  try {
    const win = window.open(url, '_blank', 'noopener,noreferrer');
    // If popup was blocked, win will be null
    if (!win) {
      window.location.href = url;
    }
  } catch {
    window.location.href = url;
  }
}

/**
 * Copy text to clipboard with execCommand fallback.
 * Returns true on success, false on failure.
 */
export async function copyTextWithFallback(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback for older browsers / iframe contexts
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  }
}

/**
 * High-level: copy text and show appropriate toast.
 */
export async function copyWithToast(
  text: string,
  successMsg = 'Copied!',
  failMsg = 'Failed to copy',
): Promise<boolean> {
  const ok = await copyTextWithFallback(text);
  if (ok) {
    toast.success(successMsg);
  } else {
    toast.error(failMsg);
  }
  return ok;
}
